import { supabase } from '../utils/supabaseClient';
import { BASE_API_URL } from '../utils/fetch';
import { Media } from '../types/Media';

export interface PersonalMediaResponse {
  limit: number;
  offset: number;
  total: number;
  images: Media[];
}

const getAccessToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
};

export const listUserMedia = async (
  limit = 20,
  offset = 0,
  search?: string,
): Promise<PersonalMediaResponse> => {
  const token = await getAccessToken();
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search?.trim()) params.append('search', search);

  const response = await fetch(`${BASE_API_URL}user-media?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Failed to list user media: ${response.status}`);
  return response.json();
};

export const uploadUserMedia = async (files: File[]): Promise<{ message: string; uploaded_files: { file_name: string; file_url: string }[] }> => {
  const token = await getAccessToken();
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));

  const response = await fetch(`${BASE_API_URL}user-media/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
  return response.json();
};

export const deleteUserMedia = async (filename: string): Promise<void> => {
  const token = await getAccessToken();
  const response = await fetch(`${BASE_API_URL}user-media/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Delete failed: ${response.status}`);
};

export const getAuthenticatedUserImageUrl = async (imageUrl: string): Promise<string> => {
  const token = await getAccessToken();
  const response = await fetch(imageUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};
